import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FaChevronLeft, FaChevronRight, FaExclamationTriangle, FaFileInvoice, FaInfoCircle, FaTimes } from "react-icons/fa";
import { Input, Modal, notification } from "antd";
import "../style/gestionUtilisateur.css";
import "../style/gestionDemandesJetons.css";

const GestionConfirmationFinaleJetons = () => {
  const notifyError = useCallback(
    (message) => {
      notification.error({
        message: "Erreur",
        description: message,
        placement: "topRight",
      });
    },
    [],
  );
  const notifyWarning = useCallback(
    (message) => {
      notification.warning({
        message: "Attention",
        description: message,
        placement: "topRight",
      });
    },
    [],
  );
  const notifyInfo = useCallback(
    (message) => {
      notification.info({
        message: "Information",
        description: message,
        placement: "topRight",
      });
    },
    [],
  );
  const notifyStepFinalApproved = useCallback(
    () => {
      notification.success({
        message: "Paiement confirme",
        description: "Etape 4/4: confirmation finale du paiement effectuee.",
        placement: "topRight",
      });
    },
    [],
  );
  const [requests, setRequests] = useState([]);
  const [requestStatusFilter, setRequestStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const getStatusLabel = (value) => {
    if (value === "pending") return "En attente de premiere confirmation";
    if (value === "payment_pending") return "En attente d'envoi de preuve client";
    if (value === "payment_submitted") return "Preuve envoyee, attente finale";
    if (value === "approved") return "Confirmee";
    if (value === "rejected") return "Refusee";
    return "En attente";
  };

  const downloadProof = async (requestId, filename, mime) => {
    try {
      const res = await axios.get(
        `/api/admin/jeton/${requestId}/proof`,
        {
          withCredentials: true,
          responseType: "arraybuffer",
        },
      );

      const blob = new Blob([res.data], {
        type: mime || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename || `preuve_${requestId}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      notifyError(
        e?.response?.data?.message ||
          "Impossible de telecharger la preuve de paiement.",
      );
    }
  };

  const fetchTokenRequests = useCallback(async () => {
    try {
      setLoading(true);
      const query =
        requestStatusFilter && requestStatusFilter !== "all"
          ? `?status=${requestStatusFilter}`
          : "";

      const res = await axios.get(
        `/api/admin/jeton${query}`,
        { withCredentials: true },
      );

      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      notifyError(
        e?.response?.data?.message ||
          "Erreur lors du chargement des demandes de jetons.",
      );
    } finally {
      setLoading(false);
    }
  }, [notifyError, requestStatusFilter]);

  useEffect(() => {
    fetchTokenRequests();
  }, [fetchTokenRequests]);

  // Reset to page 1 on filter
  useEffect(() => {
    setCurrentPage(1);
  }, [requestStatusFilter]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = requests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(requests.length / itemsPerPage);

  const handlePageChange = (page) => setCurrentPage(page);

  const openDecisionModal = useCallback(
    ({ title, description, defaultNote, okText, danger = false }) =>
      new Promise((resolve) => {
        let note = defaultNote || "";

        const modalTitle = (
          <div className="token-decision-title-wrap">
            <span className={`token-decision-title-icon ${danger ? "danger" : "info"}`}>
              {danger ? <FaExclamationTriangle /> : <FaInfoCircle />}
            </span>
            <span>{title}</span>
          </div>
        );

        Modal.confirm({
          className: "token-decision-modal",
          centered: true,
          width: 560,
          icon: null,
          closable: true,
          closeIcon: (
            <span className="token-decision-close-icon" aria-label="Fermer">
              <FaTimes />
            </span>
          ),
          maskClosable: true,
          title: modalTitle,
          okText,
          cancelText: "Annuler",
          okButtonProps: danger ? { danger: true } : {},
          content: (
            <div className="token-decision-content">
              <p>{description}</p>
              <Input.TextArea
                autoSize={{ minRows: 3, maxRows: 6 }}
                defaultValue={defaultNote}
                placeholder="Note admin (optionnel)"
                onChange={(event) => {
                  note = event.target.value;
                }}
              />
            </div>
          ),
          onOk: () => resolve(note.trim()),
          onCancel: () => resolve(null),
        });
      }),
    [],
  );

  const handleFinalDecision = async (id, decision) => {
    const note = await openDecisionModal({
      title:
        decision === "approved"
          ? "Confirmation finale de paiement"
          : "Refuser la demande",
      description:
        decision === "approved"
          ? "Confirmez que le paiement est recu pour finaliser la demande."
          : "Cette action refusera definitivement la demande.",
      defaultNote: decision === "approved" ? "Paiement confirme" : "Demande refusee",
      okText: decision === "approved" ? "Confirmer paiement" : "Refuser",
      danger: decision === "rejected",
    });

    if (note === null) {
      notifyInfo("Action annulee.");
      return;
    }

    try {
      await axios.put(
        `/api/admin/jeton/${id}/decision`,
        {
          decision,
          admin_note: note || null,
        },
        { withCredentials: true },
      );

      if (decision === "approved") {
        notifyStepFinalApproved();
      } else {
        notifyWarning("Demande refusee avec succes.");
      }
      fetchTokenRequests();
    } catch (e) {
      notifyError(
        e?.response?.data?.message ||
          "Erreur lors du traitement de la demande.",
      );
    }
  };

  return (
    <div className="page">
      <div className="full-width-container">
        <div className="page-header">
          <div className="page-header-title">
            <FaFileInvoice />
            <h2>Confirmation finale jetons</h2>
          </div>
        </div>

        <div className="filters-card">
          <div className="token-request-filter-row">
            <label htmlFor="requestStatusFinal">Statut des demandes</label>
            <select
              id="requestStatusFinal"
              className="role-select"
              value={requestStatusFilter}
              onChange={(e) => setRequestStatusFilter(e.target.value)}
            >
              <option value="payment_submitted">Preuves envoyees (a confirmer)</option>
              <option value="approved">Confirmees</option>
              <option value="rejected">Refusees</option>
              <option value="all">Toutes</option>
            </select>
          </div>
        </div>

        <div className="transactions-container">
          {requests.length > 0 && (
            <div style={{ marginBottom: '16px', fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
              Affiche {Math.min(requests.length, currentItems.length)} sur {requests.length} demande{requests.length > 1 ? 's' : ''}
            </div>
          )}
          <div className="transactions-list">
            {loading ? (
              <div className="empty-token-request">Chargement...</div>
            ) : requests.length === 0 ? (
              <div className="empty-token-request">Aucune demande trouvee.</div>
            ) : (
              currentItems.map((request) => (
                <div key={request.id} className="transaction-row blue">
                  <div className="transaction-left">
                    <div className="transaction-number">
                      Demande #{request.id} - {request.pack_name}
                    </div>
                    <div className="transaction-meta">
                      Client: {request.user_name} ({request.user_email})
                    </div>
                    <div className="transaction-meta">
                      Jetons: {request.tokens} | Prix: {request.price_tnd} TND
                    </div>
                    <div className="transaction-meta">
                      Contact: {request.contact_info || "non renseigne"}
                    </div>
                    <div className="transaction-meta">
                      Source: {request.request_source || "pack"}
                    </div>
                    {request.created_at && (
                      <div className="transaction-meta">
                        Creee le: {new Date(request.created_at).toLocaleString("fr-FR")}
                      </div>
                    )}
                    {request.payment_uploaded_at && (
                      <div className="transaction-meta">
                        Preuve envoyee le: {new Date(request.payment_uploaded_at).toLocaleString("fr-FR")}
                      </div>
                    )}
                    <div className="transaction-meta">
                      Statut: {getStatusLabel(request.status)}
                    </div>
                    {Number(request.has_payment_proof) === 1 && (
                      <button
                        type="button"
                        className="token-proof-btn"
                        onClick={() =>
                          downloadProof(
                            request.id,
                            request.payment_proof_name || `preuve_${request.id}`,
                            request.payment_proof_mime,
                          )
                        }
                      >
                        Voir preuve paiement
                      </button>
                    )}
                    {request.admin_note && (
                      <div className="transaction-meta">
                        Note admin: {request.admin_note}
                      </div>
                    )}
                  </div>

                  <div className="transaction-actions">
                    {request.status === "payment_submitted" ? (
                      <>
                        <button
                          className="btn btn-primary"
                          onClick={() => handleFinalDecision(request.id, "approved")}
                        >
                          Confirmer paiement
                        </button>
                        <button
                          className="btn btn-outline"
                          onClick={() => handleFinalDecision(request.id, "rejected")}
                        >
                          Refuser
                        </button>
                      </>
                    ) : request.status === "pending" ||
                      request.status === "payment_pending" ? (
                      <span className="token-request-final">
                        Traiter dans "Demandes jetons"
                      </span>
                    ) : (
                      <span className="token-request-final">
                        Deja {request.status === "approved" ? "confirmee" : "refusee"}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-container" style={{ marginTop: '24px' }}>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <FaChevronLeft />
              </button>

              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  className={`pagination-number ${
                    currentPage === i + 1 ? "active" : ""
                  }`}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </button>
              ))}

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GestionConfirmationFinaleJetons;
